import { yupResolver } from "@hookform/resolvers/yup";
import omit from "lodash/omit";
import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Modal } from "ui";

import { useCreateExpense, useExpenseUpdate } from "../../../apis";
import { ExpenseMediaTypes } from "../../../constants";
import { expenseSchema, getInitialExpenseFormValues } from "../../../helpers";
import { MainInformation, PaymentProofSection } from "./atoms";
import {
  ExpenseModalProps,
  ExpensesFormValuesProps
} from "./ExpenseModal.types";

import styles from "./ExpenseModal.module.scss";

const ADD_EXPENSE_FORM_ID = "delivery-fee-form-id";

const ExpenseModal = ({ isOpen, onClose, editableItem }: ExpenseModalProps) => {
  const { t } = useTranslation();

  const { control, register, handleSubmit, formState, reset, ...methods } =
    useForm<ExpensesFormValuesProps>({
      defaultValues: getInitialExpenseFormValues(editableItem),
      resolver: yupResolver(expenseSchema)
    });

  const { mutate: mutateCreateExpense, isLoading: isLoadingCreateExpense } =
    useCreateExpense({
      onSuccess: () => {
        reset();
        onClose();

        toast.success(t("Expense was added successfully!"));
      }
    });

  const { mutate: mutateUpdateExpense, isLoading: isLoadingUpdateExpense } =
    useExpenseUpdate({
      onSuccess: () => {
        reset();
        onClose();

        toast.success(t("Expense was updated successfully!"));
      }
    });

  const onSubmit = (formData: ExpensesFormValuesProps) => {
    const { paymentProofs, expenseProofs } = formData;

    if (formData.id) {
      mutateUpdateExpense({
        ...omit(formData, ["paymentProofs", "expenseProofs"]),
        id: formData.id,
        createdAt: moment(formData.createdAt).format("YYYY-MM-DD"),
        media: [
          ...expenseProofs.map((proof) => ({
            mediaId: proof.id,
            type: ExpenseMediaTypes.Proof
          })),
          ...paymentProofs.map((proof) => ({
            mediaId: proof.id,
            type: ExpenseMediaTypes.ProofOfPayment
          }))
        ]
      });
    } else {
      mutateCreateExpense({
        ...omit(formData, ["paymentProofs", "expenseProofs"]),
        createdAt: moment(formData.createdAt).format("YYYY-MM-DD"),
        media: [
          ...expenseProofs.map((proof) => ({
            mediaId: proof.id,
            type: ExpenseMediaTypes.Proof
          })),
          ...paymentProofs.map((proof) => ({
            mediaId: proof.id,
            type: ExpenseMediaTypes.ProofOfPayment
          }))
        ]
      });
    }
  };

  return (
    <FormProvider
      {...{
        ...methods,
        control,
        register,
        handleSubmit,
        formState,
        reset
      }}
    >
      <Modal
        formId={ADD_EXPENSE_FORM_ID}
        isOpen={isOpen}
        title={editableItem ? "Edit Expense" : "New Expense"}
        okText={editableItem ? "Edit" : "Add"}
        isLoading={isLoadingCreateExpense || isLoadingUpdateExpense}
        onClose={onClose}
        onOk={handleSubmit(onSubmit)}
        className={styles.modal}
        contentClassName={styles.modal__content}
      >
        <MainInformation />

        <PaymentProofSection />
      </Modal>
    </FormProvider>
  );
};

export default ExpenseModal;
